import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Card, CardContent, Typography, Alert, CircularProgress } from '@mui/material';
import useDataFetch, { usePostDataFetch } from '../utils/useDataFetch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const ApproveTeamWithCode = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const [code, setCode] = useState('');
    const [submitted, setSubmitted] = useState(false);

    // Lade Team-Info (ohne Auth nötig, nur zur Info)
    const { data: teamData, isLoading: teamLoading } = useDataFetch({ url: `/teams/${teamId}` });
    const { data: approveData, error: approveError, isLoading: approveLoading, postData: submitCode } = usePostDataFetch();

    const handleSubmit = (e) => {
        e.preventDefault();
        if (code.length !== 8) {
            alert('Code muss 8 Zeichen lang sein');
            return;
        }
        setSubmitted(true);
        submitCode({ url: `/admin/approve-team-code/${teamId}`, aBody: { approvalCode: code }, skipAuth: true });
    };

    if (teamLoading) return <CircularProgress />;

    const team = teamData?.team || {};

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600, margin: '0 auto' }}>
            <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                Team-Genehmigung
            </Typography>

            {approveData?.success ? (
                <Card sx={{ borderLeft: '5px solid #4CAF50', bgcolor: '#e8f5e9' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                        <CheckCircleIcon sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
                        <Typography variant="h5" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>
                            Team genehmigt! ✓
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Das Team "{team.name}" wurde erfolgreich genehmigt und ist jetzt aktiv.
                        </Typography>
                        <Button variant="contained" color="success" onClick={() => navigate('/')} fullWidth>
                            Zur Startseite
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Bestätigungscode erforderlich
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {team.name ? `Team: ${team.name}` : 'Code eingeben um das Team zu genehmigen'}
                        </Typography>

                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="Bestätigungscode"
                                placeholder="z.B. AB12CD34"
                                value={code.toUpperCase()}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                inputProps={{ maxLength: 8, style: { textAlign: 'center', fontSize: '1.3em', letterSpacing: '4px', fontWeight: 'bold' } }}
                                disabled={approveLoading || submitted}
                                sx={{ mb: 2 }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                type="submit"
                                disabled={code.length !== 8 || approveLoading || submitted}
                                sx={{ py: 1.5 }}
                            >
                                {approveLoading ? 'Wird überprüft...' : 'Code bestätigen'}
                            </Button>
                        </form>

                        {approveError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <ErrorIcon sx={{ mt: 0.5, flexShrink: 0 }} />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                            Fehler
                                        </Typography>
                                        <Typography variant="body2">
                                            {approveError}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}

            <Card sx={{ bgcolor: '#f5f5f5' }}>
                <CardContent>
                    <Typography variant="caption" color="text.secondary">
                        💡 <strong>Tipp:</strong> Der Code wurde per Email versendet. Bitte überprüfe dein Postfach (und den Spam-Ordner).
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ApproveTeamWithCode;
